/**
 * AttachmentBadge — iter-169x Phase 3.
 *
 * Small thumbnail rendered inline next to a movement row when the AI
 * receipt OCR feature persisted the original image. Click opens a
 * dialog showing the full receipt (image or embedded PDF).
 *
 * The backend ships `attachment.thumb_url` and `attachment.file_url`
 * relative to /api; we resolve them against REACT_APP_BACKEND_URL and
 * add the auth header via the shared axios client when fetching.
 */
import { useState } from "react";
import { Paperclip, FileText, ExternalLink, X } from "lucide-react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { api } from "../lib/api";

/** Fetch a protected attachment URL and return an object-URL blob.
 *  Browsers can't pass Authorization headers via <img src="">, so we
 *  proxy via axios → blob → URL.createObjectURL. */
async function fetchAsObjectUrl(relPath) {
  if (!relPath) return null;
  // `api` already prefixes /api, so strip it here.
  const cleaned = relPath.replace(/^\/api/, "");
  const resp = await api.get(cleaned, { responseType: "blob" });
  return URL.createObjectURL(resp.data);
}

export function AttachmentBadge({ attachment, testid }) {
  const [open, setOpen] = useState(false);
  const [thumb, setThumb] = useState(null);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!attachment) return null;
  const isPdf = attachment.mime_type === "application/pdf";

  // Lazy-load the thumb only when the badge first paints in viewport.
  const ensureThumb = async () => {
    if (thumb || !attachment.has_thumb) return;
    try {
      const u = await fetchAsObjectUrl(attachment.thumb_url);
      setThumb(u);
    } catch (e) {
      console.warn("thumb fetch failed", e);
    }
  };

  const openModal = async () => {
    setOpen(true);
    if (full || loading) return;
    setLoading(true);
    try {
      const u = await fetchAsObjectUrl(attachment.file_url);
      setFull(u);
    } catch (e) {
      console.warn("attachment fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={openModal}
        onMouseEnter={ensureThumb}
        onFocus={ensureThumb}
        data-testid={testid || `attachment-${attachment.id}`}
        className="relative h-10 w-10 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 hover:border-emerald-400 transition-colors shrink-0 group"
        title="Ver comprobante"
      >
        {thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-cover" />
        ) : isPdf ? (
          <span className="flex h-full w-full items-center justify-center text-slate-500">
            <FileText className="h-4 w-4" />
          </span>
        ) : (
          <span className="flex h-full w-full items-center justify-center text-slate-400">
            <Paperclip className="h-3.5 w-3.5" />
          </span>
        )}
        <span className="absolute inset-0 bg-emerald-900/0 group-hover:bg-emerald-900/10 transition-colors" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl p-0 overflow-hidden bg-white"
          data-testid={`attachment-${attachment.id}-modal`}
        >
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">
              Comprobante
            </div>
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 text-slate-400 hover:text-slate-700"
              data-testid={`attachment-${attachment.id}-modal-close`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="bg-slate-50 flex items-center justify-center min-h-[280px] max-h-[78vh] overflow-auto">
            {loading && <div className="p-10 text-slate-400 text-sm">Cargando…</div>}
            {full && !loading && (
              isPdf ? (
                <object data={full} type="application/pdf" className="w-full h-[78vh]">
                  <a
                    href={full} download
                    className="inline-flex items-center gap-1 text-emerald-700 text-sm font-medium m-6"
                  >
                    Descargar PDF <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </object>
              ) : (
                <img src={full} alt="" className="max-w-full max-h-[78vh] object-contain" />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
